import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, Redirect, useParams } from "react-router-dom";
import axios from "axios";
import {
  FaRoute,
  FaSearch,
  FaSyncAlt,
  FaMapMarkerAlt,
  FaRoad,
  FaArrowRight,
  FaArrowLeft,
  FaBus,
  FaUserGraduate,
  FaPhone,
  FaClock,
  FaEdit,
  FaTrash,
  FaUsers,
} from "react-icons/fa";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import COLORS from "../resources/colors/COLORS";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import MdlEditTransportRoute from "../dialog/MdlEditTransportRoute";
import MdlEditPickupPoint from "../dialog/MdlEditPickupPoint";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return Math.round(n).toLocaleString("en-IN");
}

function formatDistance(raw) {
  if (raw == null || raw === "") return null;
  const value = String(raw).trim();
  if (!value) return null;
  if (/km/i.test(value)) return value;
  const n = Number(value);
  if (Number.isFinite(n)) return `${n} km`;
  return value;
}

function formatPickupTime(raw) {
  if (!raw) return null;
  const value = String(raw).trim();
  const m = value.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return value;
  let h = parseInt(m[1], 10);
  const min = m[2];
  if (Number.isNaN(h)) return value;
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${min} ${ampm}`;
}

function studentInitials(name) {
  const parts = String(name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || "?").toUpperCase();
}

function resolveSessionId() {
  const session = SessionManager.getCurrentSession();
  if (Array.isArray(session) && session[0]?.session_id) {
    return String(session[0].session_id);
  }
  if (session && typeof session === "object" && session.session_id) {
    return String(session.session_id);
  }
  return "";
}

const StudentAvatar = memo(function StudentAvatar({ name, imagePath }) {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [imagePath]);

  const showImg = Boolean(imagePath) && !imgFailed;

  return (
    <div className="trd-student__avatar" aria-hidden>
      {showImg ? (
        <img src={imagePath} alt="" onError={() => setImgFailed(true)} />
      ) : (
        <span>{studentInitials(name)}</span>
      )}
    </div>
  );
});

const StudentCard = memo(function StudentCard({ student, index }) {
  const classLabel =
    student.class_label ||
    [student.class_name, student.section_name].filter(Boolean).join(" · ") ||
    "—";
  const vehicle =
    student.vehicle_number || student.vehicle_name || null;
  const pickupTime = formatPickupTime(student.pickup_time);

  return (
    <article className="trd-student" style={{ "--trd-i": index }}>
      <span className="trd-student__index" aria-hidden>
        {String(index + 1).padStart(2, "0")}
      </span>
      <StudentAvatar
        name={student.student_name}
        imagePath={student.student_img_path}
      />
      <div className="trd-student__main min-w-0">
        <h3 className="trd-student__name">
          {student.student_name || "N/A"}
        </h3>
        <p className="trd-student__father">
          {student.father_name ? `S/O ${student.father_name}` : "—"}
        </p>
        <div className="trd-student__chips">
          <span className="trd-chip trd-chip--id">
            <FaUserGraduate aria-hidden />
            {student.student_id || "—"}
            {student.student_roll_no
              ? ` · Roll ${student.student_roll_no}`
              : ""}
          </span>
          <span className="trd-chip trd-chip--class">{classLabel}</span>
          {student.pickup_point ? (
            <span className="trd-chip trd-chip--stop" title={student.pickup_point}>
              <FaMapMarkerAlt aria-hidden />
              {student.pickup_point}
            </span>
          ) : null}
          {pickupTime ? (
            <span className="trd-chip trd-chip--time">
              <FaClock aria-hidden />
              {pickupTime}
            </span>
          ) : null}
          {vehicle ? (
            <span className="trd-chip trd-chip--bus">
              <FaBus aria-hidden />
              {vehicle}
            </span>
          ) : null}
          {student.mobile_no ? (
            <span className="trd-chip trd-chip--phone">
              <FaPhone aria-hidden />
              {student.mobile_no}
            </span>
          ) : null}
        </div>
      </div>
    </article>
  );
});

const TransportRoutesDetails = () => {
  const loggedIn = localStorage.getItem("token") != null;
  const { route_id } = useParams();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [route, setRoute] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showEditRoute, setShowEditRoute] = useState(false);
  const [editPickup, setEditPickup] = useState(null);
  const [deletingPickupId, setDeletingPickupId] = useState(null);
  const abortRef = useRef(null);

  const loadRouteDetails = useCallback(
    async ({ soft = false } = {}) => {
      if (!route_id) {
        setRoute(null);
        setLoading(false);
        return;
      }

      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        setRoute(null);
        myToast.notify_warning("School code is missing.");
        setLoading(false);
        setRefreshing(false);
        return;
      }

      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      if (soft) setRefreshing(true);
      else setLoading(true);

      try {
        const params = {
          school_code: schoolCode,
          route_id,
        };
        const sessionId = resolveSessionId();
        if (sessionId) params.session_id = sessionId;

        const res = await axios.get(`${API_BASE()}getRouteDetails.php`, {
          params,
          signal: controller.signal,
        });

        const payload = res?.data;
        if (payload?.api_status === "OK" && Array.isArray(payload.data) && payload.data[0]) {
          setRoute(payload.data[0]);
        } else {
          setRoute(null);
          if (payload?.api_remark) {
            myToast.notify_warning(payload.api_remark);
          }
        }
      } catch (error) {
        if (
          axios.isCancel?.(error) ||
          error?.name === "CanceledError" ||
          error?.code === "ERR_CANCELED"
        ) {
          return;
        }
        console.error("[TransportRoutesDetails] load error", error);
        setRoute(null);
        myToast.notify_warning("Failed to load route details.");
      } finally {
        if (abortRef.current === controller) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [route_id]
  );

  useEffect(() => {
    loadRouteDetails();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [loadRouteDetails]);

  const handleDeletePickup = useCallback(
    async (point) => {
      const pickupPointId = point?.pickup_point_id;
      if (!route_id || !pickupPointId) {
        myToast.notify_warning("Pickup point id is missing.");
        return;
      }

      const studentCount = Number(point?.student_count) || 0;
      if (studentCount > 0) {
        myToast.notify_warning(
          "Cannot delete: students are assigned to this pickup point."
        );
        return;
      }

      const label = point?.pickup_point_name || "this pickup point";
      if (
        !window.confirm(
          `Delete "${label}"? This cannot be undone if students are later reassigned.`
        )
      ) {
        return;
      }

      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        myToast.notify_warning("School code is missing.");
        return;
      }

      setDeletingPickupId(String(pickupPointId));
      try {
        const params = new URLSearchParams();
        params.append("school_code", schoolCode);
        params.append("route_id", route_id);
        params.append("pickup_point_id", pickupPointId);
        const sessionId = resolveSessionId();
        if (sessionId) params.append("session_id", sessionId);

        const response = await axios.post(
          `${API_BASE()}deletePickupPoint.php`,
          params
        );

        if (response.data?.api_status === "OK") {
          myToast.notify_success("Pickup point deleted");
          setRoute((prev) => {
            if (!prev) return prev;
            const nextPoints = (Array.isArray(prev.pickup_points)
              ? prev.pickup_points
              : []
            ).filter(
              (p) => String(p?.pickup_point_id) !== String(pickupPointId)
            );
            return { ...prev, pickup_points: nextPoints };
          });
        } else {
          myToast.notify_warning(
            response.data?.api_remark || "Could not delete pickup point"
          );
        }
      } catch (error) {
        console.error("[TransportRoutesDetails] delete pickup error", error);
        myToast.notify_error("Failed to delete pickup point");
      } finally {
        setDeletingPickupId(null);
      }
    },
    [route_id]
  );

  const pickupPoints = useMemo(() => {
    const list = Array.isArray(route?.pickup_points) ? [...route.pickup_points] : [];
    return list.sort((a, b) => {
      const da = Number(a?.distance_from_school);
      const db = Number(b?.distance_from_school);
      const aOk = Number.isFinite(da);
      const bOk = Number.isFinite(db);
      if (aOk && bOk && da !== db) return da - db;
      if (aOk && !bOk) return -1;
      if (!aOk && bOk) return 1;
      return String(a?.pickup_point_name || "").localeCompare(
        String(b?.pickup_point_name || "")
      );
    });
  }, [route]);

  const vehicles = useMemo(
    () => (Array.isArray(route?.vehicles_on_route) ? route.vehicles_on_route : []),
    [route]
  );

  const students = useMemo(
    () => (Array.isArray(route?.students) ? route.students : []),
    [route]
  );

  const filteredStudents = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return students;
    return students.filter((student) => {
      const hay = [
        student.student_name,
        student.student_id,
        student.father_name,
        student.class_name,
        student.section_name,
        student.class_label,
        student.pickup_point,
        student.mobile_no,
        student.vehicle_number,
        student.vehicle_name,
        student.student_roll_no,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [students, searchTerm]);

  const distance = formatDistance(route?.route_distance);
  const vehicleLabel = vehicles
    .map((v) => v.vehicle_number || v.vehicle_name)
    .filter(Boolean)
    .join(" · ");

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page trd-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Route Details</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaRoute />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">
                {route?.route_name || (loading ? "Loading…" : "Route details")}
              </h1>
              <p className="sm-hero__sub">
                {route
                  ? `${route.start_from || "—"} → ${route.end_to || "—"} · ${formatCount(
                      pickupPoints.length
                    )} stops · ${formatCount(students.length)} students`
                  : "View route, stops, and assigned students"}
              </p>
            </div>
          </div>

          <div className="sm-hero__tools">
            <div className="sm-hero__search-group">
              <FaSearch className="trd-search-icon" aria-hidden />
              <input
                type="search"
                className="sm-hero__search"
                placeholder="Search students…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search route students"
                disabled={!students.length}
              />
            </div>
            <button
              type="button"
              onClick={() => loadRouteDetails({ soft: true })}
              disabled={refreshing || loading}
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh"
              aria-label="Refresh route details"
            >
              <FaSyncAlt aria-hidden />
            </button>
            {route ? (
              <button
                type="button"
                className="trd-hero-link trd-hero-link--solid"
                onClick={() => setShowEditRoute(true)}
              >
                <FaEdit aria-hidden /> Edit Route
              </button>
            ) : null}
            <Link to="/AdminMain/TransportRoutes" className="trd-hero-link">
              <FaArrowLeft aria-hidden /> Routes
            </Link>
          </div>
        </header>

        <div className="trd-content">
          {loading ? (
            <div className="trd-loading">
              <MyLoader />
            </div>
          ) : !route ? (
            <div className="sm-empty trd-empty">
              <div className="trd-empty__circle" aria-hidden>
                <FaRoute />
              </div>
              <p className="sm-empty__title">Route not found</p>
              <p className="sm-empty__hint">
                This route may have been removed or is inactive.
              </p>
              <Link to="/AdminMain/TransportRoutes" className="trd-empty-cta">
                <FaArrowLeft aria-hidden /> Back to routes
              </Link>
            </div>
          ) : (
            <>
              <section className="trd-panel" aria-label="Route information">
                <div className="trd-panel__head">
                  <h2 className="trd-panel__title">Route information</h2>
                </div>
                <div className="trd-meta">
                  <div className="trd-meta__item">
                    <span className="trd-meta__label">From</span>
                    <span className="trd-meta__value">
                      {route.start_from || "—"}
                    </span>
                  </div>
                  <div className="trd-meta__arrow" aria-hidden>
                    <FaArrowRight />
                  </div>
                  <div className="trd-meta__item">
                    <span className="trd-meta__label">To</span>
                    <span className="trd-meta__value">
                      {route.end_to || "—"}
                    </span>
                  </div>
                  <div className="trd-meta__item">
                    <span className="trd-meta__label">Distance</span>
                    <span className="trd-meta__value">
                      {distance || "—"}
                    </span>
                  </div>
                  <div className="trd-meta__item">
                    <span className="trd-meta__label">Vehicle</span>
                    <span className="trd-meta__value">
                      {vehicleLabel || "—"}
                    </span>
                  </div>
                </div>
              </section>

              <section className="trd-panel" aria-label="Pickup points">
                <div className="trd-panel__head">
                  <div className="min-w-0">
                    <h2 className="trd-panel__title">
                      <FaMapMarkerAlt aria-hidden /> Pickup points
                    </h2>
                    <p className="trd-panel__sub">
                      Stops on this route · edit anytime · delete when empty
                    </p>
                  </div>
                  <span className="trd-panel__count">
                    {formatCount(pickupPoints.length)} stop
                    {pickupPoints.length === 1 ? "" : "s"}
                  </span>
                </div>
                {pickupPoints.length === 0 ? (
                  <div className="trd-stops-empty">
                    <div className="trd-stops-empty__icon" aria-hidden>
                      <FaMapMarkerAlt />
                    </div>
                    <p className="trd-stops-empty__title">No pickup points yet</p>
                    <p className="trd-stops-empty__hint">
                      Add stops when creating or editing this route.
                    </p>
                  </div>
                ) : (
                  <div className="trd-stops" role="list">
                    {pickupPoints.map((point, i) => {
                      const pointDistance = formatDistance(
                        point?.distance_from_school
                      );
                      const studentCount = Number(point?.student_count) || 0;
                      const canDelete = studentCount === 0;
                      const isDeleting =
                        String(deletingPickupId) ===
                        String(point?.pickup_point_id);
                      return (
                        <article
                          key={point.pickup_point_id || i}
                          className={`trd-stop${
                            studentCount > 0 ? " trd-stop--has-students" : ""
                          }`}
                          role="listitem"
                          style={{ "--trd-stop-i": i }}
                        >
                          <span className="trd-stop__index" aria-hidden>
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <span className="trd-stop__marker" aria-hidden>
                            <FaMapMarkerAlt />
                          </span>
                          <div className="trd-stop__body min-w-0">
                            <h3
                              className="trd-stop__name"
                              title={point.pickup_point_name || "Stop"}
                            >
                              {point.pickup_point_name || "Unnamed stop"}
                            </h3>
                            <div className="trd-stop__chips">
                              <span
                                className={`trd-stop__chip trd-stop__chip--students${
                                  studentCount > 0
                                    ? " trd-stop__chip--students-on"
                                    : ""
                                }`}
                                title={`${studentCount} student${
                                  studentCount === 1 ? "" : "s"
                                }`}
                              >
                                <FaUsers aria-hidden />
                                {formatCount(studentCount)} student
                                {studentCount === 1 ? "" : "s"}
                              </span>
                              {pointDistance ? (
                                <span className="trd-stop__chip trd-stop__chip--dist">
                                  <FaRoad aria-hidden />
                                  {pointDistance}
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <div className="trd-stop__actions">
                            <button
                              type="button"
                              className="trd-stop__btn trd-stop__btn--edit"
                              onClick={() => setEditPickup(point)}
                              disabled={isDeleting}
                              title="Edit pickup point"
                            >
                              <FaEdit aria-hidden />
                              <span>Edit</span>
                            </button>
                            <button
                              type="button"
                              className="trd-stop__btn trd-stop__btn--delete"
                              onClick={() => handleDeletePickup(point)}
                              disabled={!canDelete || isDeleting}
                              title={
                                canDelete
                                  ? "Delete pickup point"
                                  : "Remove students from this stop before deleting"
                              }
                            >
                              <FaTrash aria-hidden />
                              <span>{isDeleting ? "…" : "Delete"}</span>
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </section>
              <section className="trd-panel" aria-label="Route students">
                <div className="trd-panel__head">
                  <div className="min-w-0">
                    <h2 className="trd-panel__title">
                      <FaUserGraduate aria-hidden /> Students on route
                    </h2>
                    <p className="trd-panel__sub">
                      Assigned transport students for the current session
                    </p>
                  </div>
                  <span className="trd-panel__count">
                    {`${filteredStudents.length}${
                      searchTerm.trim() ? ` of ${students.length}` : ""
                    } student${filteredStudents.length === 1 ? "" : "s"}`}
                  </span>
                </div>

                {filteredStudents.length === 0 ? (
                  <div className="sm-empty trd-empty trd-empty--inline">
                    <div className="trd-empty__circle" aria-hidden>
                      <FaUserGraduate />
                    </div>
                    <p className="sm-empty__title">
                      {students.length === 0
                        ? "No students on this route"
                        : "No matches for your search"}
                    </p>
                    <p className="sm-empty__hint">
                      {students.length === 0
                        ? "Assign students from Transport Students to see them here."
                        : "Try a different name, class, pickup, or vehicle."}
                    </p>
                  </div>
                ) : (
                  <div className="trd-students" role="list">
                    {filteredStudents.map((student, index) => (
                      <StudentCard
                        key={student.student_id || index}
                        student={student}
                        index={index}
                      />
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>

      {showEditRoute && route ? (
        <MdlEditTransportRoute
          route={route}
          actionClose={() => setShowEditRoute(false)}
          onSuccess={(updated) => {
            setRoute((prev) => (prev ? { ...prev, ...updated } : updated));
            setShowEditRoute(false);
          }}
        />
      ) : null}

      {editPickup ? (
        <MdlEditPickupPoint
          routeId={route_id}
          point={editPickup}
          actionClose={() => setEditPickup(null)}
          onSuccess={(updated) => {
            setRoute((prev) => {
              if (!prev) return prev;
              const nextPoints = (Array.isArray(prev.pickup_points)
                ? prev.pickup_points
                : []
              ).map((p) =>
                String(p?.pickup_point_id) === String(updated?.pickup_point_id)
                  ? { ...p, ...updated }
                  : p
              );
              return { ...prev, pickup_points: nextPoints };
            });
            setEditPickup(null);
          }}
        />
      ) : null}
    </div>
  );
};

export default TransportRoutesDetails;
